import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Target, BookOpen } from "lucide-react";
import KeywordResearch, { type KeywordSuggestion } from "./KeywordResearch";
import KeywordCompetitor from "./KeywordCompetitor";
import KeywordFavorites from "./KeywordFavorites";
import ArticleComposer from "./ArticleComposer";
import DraftList from "./DraftList";
import ArticleAudit from "./ArticleAudit";
import AdminGuideArticles from "@/components/admin/AdminGuideArticles";
import AdminGuideImages from "@/components/admin/AdminGuideImages";
import GuideDashboard from "./GuideDashboard";

const GuideHub = () => {
  const [stack, setStack] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tab, setTab] = useState("dashboard");
  const [publishedToday, setPublishedToday] = useState(0);

  const loadStats = async () => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const { count } = await (supabase as any).from("blog_articles")
      .select("id", { count: "exact", head: true })
      .eq("status", "published")
      .gte("published_at", start.toISOString());
    setPublishedToday(count || 0);
  };
  useEffect(() => { loadStats(); }, [tab]);

  const addToStack = (kw: string) => {
    setStack((s) => (s.includes(kw) ? s : [...s, kw]));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-2 border-b border-border">
        <div className="h-10 w-10 rounded-lg bg-gradient-gold flex items-center justify-center">
          <BookOpen className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">Maroc guide</h1>
          <p className="text-sm text-muted-foreground">Blog & SEO management — recherche, rédaction IA, audit, publication.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Target className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Objectif : 4 articles par jour</p>
            <p className="text-xs text-muted-foreground">{publishedToday >= 4 ? "🎉 Objectif atteint aujourd'hui !" : `${publishedToday}/4 articles publiés aujourd'hui`}</p>
            <Progress value={Math.min((publishedToday / 4) * 100, 100)} className="mt-2 h-2" />
          </div>
        </CardContent>
      </Card>

      {editingId ? (
        <ArticleAudit articleId={editingId} onBack={() => setEditingId(null)} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="dashboard">Tableau de bord</TabsTrigger>
            <TabsTrigger value="workspace">Recherche & Concurrence</TabsTrigger>
            <TabsTrigger value="favorites">Favoris</TabsTrigger>
            <TabsTrigger value="compose">Rédaction IA</TabsTrigger>
            <TabsTrigger value="drafts">Brouillons / Audit</TabsTrigger>
            <TabsTrigger value="published">Publiés</TabsTrigger>
            <TabsTrigger value="legacy-articles">Articles classiques</TabsTrigger>
            <TabsTrigger value="legacy-images">Images du guide</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-4 space-y-4">
            <GuideDashboard
              onOpenArticle={(id) => setEditingId(id)}
              onUseKeyword={(kw) => { addToStack(kw); setTab("compose"); }}
              onGotoCompetitor={() => setTab("workspace")}
              onGotoResearch={() => setTab("workspace")}
            />
            <DraftList filter="draft" onOpen={(id) => setEditingId(id)} />
          </TabsContent>

          <TabsContent value="workspace" className="mt-4">
            <div className="grid gap-4 xl:grid-cols-2">
              <KeywordResearch onConfirm={(k: KeywordSuggestion) => addToStack(k.keyword)} />
              <KeywordCompetitor onConfirm={(k: KeywordSuggestion) => addToStack(k.keyword)} />
            </div>
          </TabsContent>

          <TabsContent value="favorites" className="mt-4">
            <KeywordFavorites onUse={addToStack} onGenerated={(id) => setEditingId(id)} />
          </TabsContent>

          <TabsContent value="compose" className="mt-4">
            <ArticleComposer
              stack={stack}
              onRemove={(k) => setStack((s) => s.filter((x) => x !== k))}
              onClear={() => setStack([])}
              onGenerated={(id) => setEditingId(id)}
            />
          </TabsContent>

          <TabsContent value="drafts" className="mt-4">
            <DraftList filter="draft" onOpen={(id) => setEditingId(id)} />
          </TabsContent>

          <TabsContent value="published" className="mt-4">
            <DraftList filter="published" onOpen={(id) => setEditingId(id)} />
          </TabsContent>

          <TabsContent value="legacy-articles" className="mt-4">
            <AdminGuideArticles />
          </TabsContent>

          <TabsContent value="legacy-images" className="mt-4">
            <AdminGuideImages />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default GuideHub;